// Words for the guest-orders board (slice 66, ADR 0066). Pure, so the sentences an
// owner reads are tested without a page.

import type {
  GuestBoardFilter,
  GuestBoardOrder,
  GuestKitchenState,
  GuestRejectReason,
} from '../../api/types.js';

export const FILTERS: readonly { readonly value: GuestBoardFilter; readonly label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Not accepted' },
  { value: 'expired', label: 'Not answered' },
];

export const WINDOWS: readonly { readonly hours: number; readonly label: string }[] = [
  { hours: 24, label: 'Last 24 hours' },
  { hours: 72, label: 'Last 3 days' },
  { hours: 168, label: 'Last 7 days' },
];

const KITCHEN: Readonly<Record<GuestKitchenState, string>> = {
  waiting: 'Accepted — waiting for the kitchen',
  preparing: 'Being prepared',
  ready: 'Ready',
  served: 'Served',
};

const REJECT: Readonly<Record<GuestRejectReason, string>> = {
  out_of_stock: 'Not accepted: something was out of stock',
  kitchen_closed: 'Not accepted: the kitchen was closed',
  wrong_table: 'Not accepted: wrong table',
  other: 'Not accepted',
};

export function statusTone(status: GuestBoardOrder['status']): 'ok' | 'warn' | 'bad' | undefined {
  switch (status) {
    case 'accepted':
      return 'ok';
    case 'waiting':
      return 'warn';
    case 'expired':
    case 'rejected':
      return 'bad';
  }
}

export function statusLabel(status: GuestBoardOrder['status']): string {
  switch (status) {
    case 'waiting':
      return 'Waiting';
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Not accepted';
    case 'expired':
      return 'Not answered';
  }
}

/** One sentence on how this order stands, for the line under its items. */
export function orderSummary(o: GuestBoardOrder): string {
  switch (o.status) {
    case 'waiting':
      return 'Waiting for the cashier to accept it on the till.';
    case 'expired':
      return 'Nobody accepted it in time, so the guest was told to ask staff.';
    case 'rejected':
      return REJECT[o.rejectReason ?? 'other'];
    case 'accepted':
      return KITCHEN[o.kitchenState ?? 'waiting'];
  }
}

export function ago(iso: string, nowMs: number): string {
  const secs = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * The one thing worth saying out loud when orders arrive and none is being answered:
 * the usual cause is a till that is off or not connected.
 */
export function stuckHint(counts: Readonly<Record<string, number>>): string | null {
  const answered = (counts.accepted ?? 0) + (counts.rejected ?? 0);
  if ((counts.expired ?? 0) > 0 && answered === 0) {
    return 'Guests are ordering but no order has been answered. Check that the cashier till is switched on, online and has the Guests tab open.';
  }
  return null;
}
